import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { DashboardIconButton } from "../primitives/DashboardIconButton";
import { useModalPerf } from "../../hooks/useModalPerf";

export function DashboardModalFrame({
  title,
  subtitle,
  children,
  onClose,
  frameless = false,
  wide = false,
  left,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  frameless?: boolean;
  wide?: boolean;
  left?: ReactNode;
}) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useModalPerf(`DashboardModalFrame(${title})`);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="dashboard-modal-scrim"
      onClick={onClose}
      role="presentation"
      data-oid="6lwvib2"
    >
      <div
        aria-describedby={subtitle ? "dashboard-modal-subtitle" : undefined}
        aria-labelledby={title ? "dashboard-modal-title" : undefined}
        aria-modal="true"
        className={`dashboard-modal ${frameless ? 'dashboard-modal--frameless' : ''} ${wide ? 'dashboard-modal--wide' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        data-oid="w6mtbiv"
      >
        <header className="dashboard-modal__header" data-oid="3wpo4sb">
          <div className="flex w-9 shrink-0 items-start" data-oid="left-slot-placeholder">
            {left}
          </div>
          <div className="min-w-0 flex-1 text-center" data-oid="2y5nok:">
            <h2
              className="dashboard-modal__title"
              id="dashboard-modal-title"
              data-oid="k2.9vxc"
            >
              {title}
            </h2>
            {subtitle ? (
              <p
                className="dashboard-modal__subtitle"
                id="dashboard-modal-subtitle"
                data-oid="pv3yx1s"
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          <DashboardIconButton
            aria-label="Close modal"
            className="w-9 shrink-0"
            onClick={onClose}
            ref={closeButtonRef}
            type="button"
            data-oid="lkxmitc"
          >
            <span className="material-symbols-outlined" data-oid="ag:he1r">
              close
            </span>
          </DashboardIconButton>
        </header>

        <div className="dashboard-modal__body" data-oid="ptrcy3c">
          {children}
        </div>

        <div className="dashboard-modal__handle" data-oid="rk75i.7" />
      </div>
    </div>,
    document.body,
  );
}
